import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiCreditCard, FiArrowRight, FiCheck, FiPackage,
  FiUsers, FiCalendar
} from 'react-icons/fi';
import PublicPageHeader from '../../../components/common/PublicPageHeader';
import PublicPageFooter from '../../../components/common/PublicPageFooter';
import DocsSidebar from '../../../components/docs/DocsSidebar';

const OrgBilling = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />
      
      <div className="flex">
        <DocsSidebar currentPath="/docs/organization/billing" />
        
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <Link to="/docs" className="hover:text-blue-600">Docs</Link>
            <span>/</span>
            <Link to="/docs/organization" className="hover:text-blue-600">Organization</Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">Billing</span>
          </nav>

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex p-2 bg-violet-100 dark:bg-violet-900/30 rounded-xl mb-4">
              <FiCreditCard className="w-6 h-6 text-violet-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Billing & Subscription</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Manage your organization's subscription, billing, and payment settings.
            </p>
          </div>

          {/* Plans Overview */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Available Plans</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Free</h3>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mb-4">$0<span className="text-sm font-normal text-gray-500 dark:text-gray-400">/month</span></p>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                  <li className="flex items-center space-x-2">
                    <FiCheck className="text-green-500" />
                    <span>Up to 5 users</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <FiCheck className="text-green-500" />
                    <span>Basic features</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <FiCheck className="text-green-500" />
                    <span>Community support</span>
                  </li>
                </ul>
              </div>
              <div className="border-2 border-blue-500 rounded-xl p-6 relative">
                <span className="absolute -top-3 left-4 px-2 bg-blue-500 text-white text-xs rounded">Popular</span>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Pro</h3>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mb-4">$5<span className="text-sm font-normal text-gray-500 dark:text-gray-400">/user/month</span></p>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                  <li className="flex items-center space-x-2">
                    <FiCheck className="text-green-500" />
                    <span>Unlimited users</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <FiCheck className="text-green-500" />
                    <span>Advanced features</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <FiCheck className="text-green-500" />
                    <span>Priority support</span>
                  </li>
                </ul>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Enterprise</h3>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Custom</p>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                  <li className="flex items-center space-x-2">
                    <FiCheck className="text-green-500" />
                    <span>Custom pricing</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <FiCheck className="text-green-500" />
                    <span>SSO & SAML</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <FiCheck className="text-green-500" />
                    <span>Dedicated support</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-4 text-center">
              <Link to="/pricing" className="text-blue-600 hover:underline">View detailed pricing →</Link>
            </div>
          </section>

          {/* Managing Subscription */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Managing Your Subscription</h2>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6">
              <ol className="space-y-4">
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-violet-600 text-white rounded-full flex items-center justify-center text-sm">1</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Go to Billing Settings</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Organization Settings → Billing</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-violet-600 text-white rounded-full flex items-center justify-center text-sm">2</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">View Current Plan</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">See your current subscription details and usage</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-violet-600 text-white rounded-full flex items-center justify-center text-sm">3</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Upgrade or Modify</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Change plans or add/remove seats</p>
                  </div>
                </li>
              </ol>
            </div>
          </section>

          {/* Billing Features */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Billing Features</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiCalendar className="w-6 h-6 text-blue-600 mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Invoice History</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">View and download past invoices</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiCreditCard className="w-6 h-6 text-green-600 mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Payment Methods</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Manage credit cards and payment options</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiUsers className="w-6 h-6 text-purple-600 mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Seat Management</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Add or remove user seats as needed</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiPackage className="w-6 h-6 text-orange-600 mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Plan Comparison</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Compare features across plans</p>
              </div>
            </div>
          </section>

          {/* Navigation */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Related Guides</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/docs/organization/finance"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Organization Finance</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/pricing"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Pricing Page</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">Last updated: January 27, 2026</div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Was this helpful?</span>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">Yes</button>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">No</button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <PublicPageFooter />
    </div>
  );
};

export default OrgBilling;
